import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import FilterButton from '../../SharedComponents/FilterButton';
import FilterDropdown from '../../SharedComponents/FilterDropdown';
import MobileModal from '../../SharedComponents/MobileModal';
import styles from '../../searchV2.scss';
import useIsMobile from '../../../../../components/hooks/useIsMobile';
import { minUtrValue, maxUtrValue, minUtrpValue, maxUtrpValue } from '../../../../../utils/constants';
import { UI_TEXT, TEAM_TYPE_VALUES, SPORT_TYPE_VALUES } from '../../constants';

const SportTypeFilter = (props) => {
    const {
        filters,
        handleFilterUpdate,
        onMobileClick,
        hasResetFilters,
    } = props;

    const { isMobileWidth } = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [localFilterInputs, setLocalFilterInputs] = useState({});
    const wrapperRef = useRef(null);
    const { SINGLES, DOUBLES } = TEAM_TYPE_VALUES;
    const { TENNIS, PICKLEBALL } = SPORT_TYPE_VALUES;
    const { TENNIS: TENNIS_LABEL, PICKLEBALL: PICKLEBALL_LABEL, SPORT: SPORT_LABEL } = UI_TEXT.SPORT_TYPE_LABELS;

    // Sport type options
    const sportOptions = [
        { value: TENNIS, label: TENNIS_LABEL },
        { value: PICKLEBALL, label: PICKLEBALL_LABEL },
    ];

    // Check if any sport type is active
    const isTennisActive = filters?.sportTypes?.tennis;
    const isPickleballActive = filters?.sportTypes?.pickleball;
    const isActive = isTennisActive || isPickleballActive;

    // Initialize local filter inputs when modal opens or filters change normally
    useEffect(() => {
        if (isOpen) {
            const currentSportTypes = {
                tennis: filters?.sportTypes?.tennis || false,
                pickleball: filters?.sportTypes?.pickleball || false,
            };

            // Ensure at least one sport is selected
            if (!currentSportTypes.tennis && !currentSportTypes.pickleball) {
                currentSportTypes.tennis = true; // Default to tennis if neither is selected
            }

            setLocalFilterInputs(currentSportTypes);
        }
    }, [filters?.sportTypes, isOpen]);

    // Handle reset trigger - ensure at least one sport is selected
    useEffect(() => {
        if (hasResetFilters) {
            setLocalFilterInputs({
                tennis: true, // Default to tennis on reset
                pickleball: false,
            });
        }
    }, [hasResetFilters]);

    // Handle filter interactions
    const handleFilterClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(!isOpen);
    };

    const closeModal = () => {
        setIsOpen(false);
        setLocalFilterInputs({});
    };

    const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            closeModal();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
            closeModal();
        }
    };

    useEffect(() => {
        if (isOpen && !isMobileWidth) {
            const mouseDown = 'mousedown';
            const touchEnd = 'touchend';
            const keyDown = 'keydown';

            document.addEventListener(mouseDown, handleClickOutside);
            document.addEventListener(touchEnd, handleClickOutside, false);
            document.addEventListener(keyDown, handleKeyDown);
            return () => {
                document.removeEventListener(mouseDown, handleClickOutside);
                document.removeEventListener(touchEnd, handleClickOutside, false);
                document.removeEventListener(keyDown, handleKeyDown);
            };
        }
    }, [isOpen, isMobileWidth]);

    const handleOptionChange = (optionValue, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const currentValues = localFilterInputs || {};
        const newValue = !currentValues[optionValue];

        // Prevent deselecting the last selected sport
        const otherSport = optionValue === TENNIS ? PICKLEBALL : TENNIS;
        const otherSportSelected = currentValues[otherSport];

        // If trying to deselect and the other sport is not selected, prevent the change
        if (!newValue && !otherSportSelected) {
            return;
        }

        const updatedInputs = {
            ...currentValues,
            [optionValue]: newValue,
        };
        setLocalFilterInputs(updatedInputs);
    };

    const handleApply = () => {
        // Ensure at least one sport is selected
        const finalSportTypes = { ...localFilterInputs };
        if (!finalSportTypes.tennis && !finalSportTypes.pickleball) {
            finalSportTypes.tennis = true; // Default to tennis if neither is selected
        }

        // Build the complete filter update with sport-specific adjustments
        const completeFilterUpdate = {
            sportTypes: finalSportTypes,
        };

        // Apply sport-specific filter adjustments
        if (finalSportTypes.tennis && !finalSportTypes.pickleball) {
            // Only Tennis selected
            completeFilterUpdate.utrTeamType = SINGLES;
            completeFilterUpdate.utrMin = minUtrValue;
            completeFilterUpdate.utrMax = maxUtrValue;
        } else if (finalSportTypes.pickleball && !finalSportTypes.tennis) {
            // Only Pickleball selected
            completeFilterUpdate.utrTeamType = DOUBLES;
            completeFilterUpdate.utrMin = minUtrpValue;
            completeFilterUpdate.utrMax = maxUtrpValue;
        } else {
            // Both sports selected or neither is selected (defaults to tennis)
            completeFilterUpdate.utrTeamType = SINGLES;
        }

        // Use the atomic update approach to avoid state timing issues
        handleFilterUpdate(completeFilterUpdate, 'submit');

        setIsOpen(false);
    };

    const handleCancel = () => {
        closeModal();
    };

    const getButtonClasses = () => {
        const activeClass = isActive ? styles.searchFilterBtnActive : '';
        return `${styles.tagHover} ${styles.searchFilterBtn} ${activeClass} text-capitalize`;
    };

    const getButtonText = () => {
        const { tennis, pickleball } = filters?.sportTypes || {};
        if (tennis && !pickleball) {
            return TENNIS_LABEL;
        }
        if (pickleball && !tennis) {
            return PICKLEBALL_LABEL;
        }
        return SPORT_LABEL;
    };

    const isSportTypeSelected = (sportValue) => {
        return localFilterInputs[sportValue] || false;
    };

    const renderContent = () => (
        <div>
            {sportOptions.map((option) => {
                const isSelected = isSportTypeSelected(option.value);
                return (
                    <div
                        key={option.value}
                        className={`${styles.optionItem} ${isSelected ? 'selected' : ''}`}
                    >
                        <div className={styles.optionContent}>
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleOptionChange(option.value)}
                                className={styles.optionCheckbox}
                                data-testid={`search.events.filters.sport-type.option-${option.value}.checkbox`}
                            />
                            <span
                                className={styles.optionLabel}
                                onClick={() => handleOptionChange(option.value)}
                            >
                                {option.label}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopDropdown = () => (
        <FilterDropdown
            onCloseClick={handleCancel}
            handleFilterSubmit={handleApply}
            showHeader={false}
            data-testid="search.events.filters.sport-type.dropdown"
        >
            {renderContent()}
        </FilterDropdown>
    );

    // On mobile, render just a button that triggers the parent's modal
    if (isMobileWidth && onMobileClick) {
        return (
            <FilterButton
                isActive={isActive}
                onClick={onMobileClick}
            >
                {getButtonText()}
            </FilterButton>
        );
    }

    // On desktop, use custom filter implementation
    return (
        <div className={styles.filterWrapper} ref={wrapperRef}>
            <button
                type="button"
                className={getButtonClasses()}
                onClick={handleFilterClick}
                data-testid="search.events.filters.sport-type.trigger-btn"
            >
                {getButtonText()}
            </button>
            {isOpen && !isMobileWidth && renderDesktopDropdown()}
            <MobileModal
                isOpen={isOpen && isMobileWidth}
                onClose={closeModal}
                title="Sport"
                onApply={handleApply}
            >
                {renderContent()}
            </MobileModal>
        </div>
    );
};

SportTypeFilter.propTypes = {
    filters: PropTypes.instanceOf(Object).isRequired,
    handleFilterUpdate: PropTypes.func.isRequired,
    onMobileClick: PropTypes.func,
    hasResetFilters: PropTypes.bool,
};

SportTypeFilter.defaultProps = {
    onMobileClick: null,
    hasResetFilters: false,
};

export default SportTypeFilter;
